import React from 'react';

/**
 * Shared windowed pager for any paginated list page.
 *
 * Layout: « ← Prev  1 … 43 44 [45] 46 47 … 100  Next → »   [Go to…]
 *
 * The number band always includes first + last, current-2..current+2,
 * and '…' gaps between non-contiguous ranges. Jump-to-page input only
 * appears when there are more than 20 pages.
 *
 * label: header text shown left of the counter ('Leads', 'SIM calls', etc.)
 */
interface PagerProps {
  currentPage: number;
  lastPage:    number;
  total?:      number;
  label?:      string;
  onChange:    (page: number) => void;
}

export function buildPageWindow(current: number, last: number): Array<number | '…'> {
  if (last <= 7) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }
  const window: Array<number | '…'> = [];
  const start = Math.max(2, current - 2);
  const end   = Math.min(last - 1, current + 2);

  window.push(1);
  if (start > 2) window.push('…');
  for (let p = start; p <= end; p++) window.push(p);
  if (end < last - 1) window.push('…');
  window.push(last);
  return window;
}

const Pager: React.FC<PagerProps> = ({ currentPage, lastPage, total, label, onChange }) => {
  if (!lastPage || lastPage < 1) return null;
  const totalLabel = total !== undefined ? ` (${total})` : '';
  const showJump = lastPage > 20;

  return (
    <div className="pager">
      <span>
        {label ? `${label}${totalLabel}` : ''}
        {label && ' · '}Page {currentPage} of {lastPage}
      </span>
      {lastPage > 1 && (
        <div className="pager-btns" style={{ alignItems: 'center', gap: 4 }}>
          <button className="pager-btn" disabled={currentPage === 1}      onClick={() => onChange(1)}>«</button>
          <button className="pager-btn" disabled={currentPage === 1}      onClick={() => onChange(currentPage - 1)}>← Prev</button>

          {buildPageWindow(currentPage, lastPage).map((p, i) =>
            p === '…'
              ? <span key={`gap-${i}`} className="pager-btn" style={{ cursor: 'default', border: 'none' }}>…</span>
              : <button
                  key={p}
                  className={`pager-btn${p === currentPage ? ' active' : ''}`}
                  onClick={() => onChange(p as number)}
                >
                  {p}
                </button>
          )}

          <button className="pager-btn" disabled={currentPage === lastPage} onClick={() => onChange(currentPage + 1)}>Next →</button>
          <button className="pager-btn" disabled={currentPage === lastPage} onClick={() => onChange(lastPage)}>»</button>

          {showJump && (
            <input
              type="number"
              min={1}
              max={lastPage}
              placeholder="Go to…"
              style={{ width: 80, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, marginLeft: 6 }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const v = parseInt((e.target as HTMLInputElement).value, 10);
                  if (v >= 1 && v <= lastPage) {
                    onChange(v);
                    (e.target as HTMLInputElement).value = '';
                  }
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Pager;
